import styles from './scenarioMenu.module.scss';
import { useTranslation } from "react-i18next";
import { ReactComponent as AddIcon } from "../../shared/images/add.svg";
import { Accordion } from "../../shared/components/accordion/accordion";
import { useGetAllScenariosQuery } from "../../api/scenarioService";
import { useGetAllTestCaseTemplateQuery } from "../../api/testCaseTemplateService";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import {
  Scenario,
  ScenarioTestCase,
} from "../../types/scenarioTypes";
import { TransformExecuteResponse } from "../../types/clientTypes";
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { ScenarioModal } from "../scenarioModal/scenarioModal";
import {
  deleteScenario,
  deleteScenarioExecuteResponse,
  deleteScenarioExecuteResponsesByIds,
  setEditTestCaseId,
  setScenario,
  setScenarioExecuteResponse,
  updateScenarioExecuteResponse,
  updateScenarioSelectedTestCase
} from "../../store/slices/scenarioSlice";
import { deleteSelectedTestCaseTemplate } from "../../store/slices/testCaseTemplateSlice";
import { ScenarioTestCases } from "./scenarioTestCases/scenarioTestCases";
import { SearchInput } from "../../shared/components/searchInput/searchInput";
import { setShowSpinner } from "../../store/slices/mainSlice";
import { ScenarioItem } from "./scenarioItem/scenarioItem";
import { RunTestCasesButton } from "../runTestCasesButton/runTestCasesButton";
import { getTransformTestCase } from "../../utils/testCaseUtils";

export const ScenarioMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const selectedScenario = useSelector((state: RootState) => state.scenarioSlice.selectedScenario);
  const editTestCaseId = useSelector((state: RootState) => state.scenarioSlice.editTestCaseId);
  const selectedTestCases = useSelector((state: RootState) => state.scenarioSlice.selectedTestCases);

  const [activeIndex, setActiveIndex] = useState<number>()
  const [showScenarioModal, setShowScenarioModal] = useState<boolean>(false)
  const [editScenario, setEditScenario] = useState<Scenario>()
  const [searchValue, setSearchValue] = useState<string>("")
  const [filteredScenarios, setFilteredScenarios] = useState<Array<Scenario>>([]);

  const { data: scenarios, refetch: refetchScenarios, isLoading: isLoadingScenarios, isFetching: isFetchingScenarios } = useGetAllScenariosQuery("", { refetchOnMountOrArgChange: true });
  const { data: testCaseTemplates, isLoading: isLoadingTemplates, isFetching: isFetchingTemplates } = useGetAllTestCaseTemplateQuery("", { refetchOnMountOrArgChange: true });

  const transformTestCases = useCallback((editTestCaseId?: number) => {
    const newSelectedTestCases: Array<ScenarioTestCase> = [];
    selectedTestCases.forEach(testCase => {
      if (editTestCaseId != null && testCase.id !== editTestCaseId) {
        newSelectedTestCases.push(testCase);
        return;
      }

      const template = testCaseTemplates?.find(item => item.id === testCase.testCaseTemplateId);
      if (!template) {
        console.error("Error find Template");
        return;
      }

      const newTestCase = getTransformTestCase(selectedVariables, template, testCase);
      newSelectedTestCases.push({
        ...newTestCase,
        queueNumber: testCase.queueNumber,
        isDeleted: testCase.isDeleted,
        isUpdated: testCase.isUpdated,
      });
    });
    dispatch(updateScenarioSelectedTestCase(newSelectedTestCases));
  }, [selectedTestCases, testCaseTemplates, selectedVariables, dispatch]);

  useEffect(() => {
    transformTestCases();
    dispatch(deleteScenarioExecuteResponse());
  }, [selectedVariables, dispatch]);

  useEffect(() => {
    if (!isLoadingTemplates && !isFetchingTemplates) {
      transformTestCases(editTestCaseId);
      if (editTestCaseId != null) {
        const internalIds = selectedScenario.assignedRequests.filter(request => request.requestId === editTestCaseId).map(request => request.id?.toString());
        dispatch(deleteScenarioExecuteResponsesByIds(internalIds));
        dispatch(setEditTestCaseId(undefined));
      }
    }
  }, [testCaseTemplates, isLoadingTemplates, isFetchingTemplates, dispatch]);

  useEffect(() => {
    dispatch(setShowSpinner(isLoadingScenarios || isFetchingScenarios || isLoadingTemplates || isFetchingTemplates));
  }, [isLoadingScenarios, isFetchingScenarios, isLoadingTemplates, isFetchingTemplates]);

  useEffect(() => {
    if (!!filteredScenarios.length && selectedScenario.id) {
      const findIndex = filteredScenarios.findIndex(item => item.id === selectedScenario.id);
      if (findIndex > -1) {
        setActiveIndex(findIndex);
        dispatch(setScenario({scenario: filteredScenarios[findIndex], templates: testCaseTemplates || [], selectedVariables}));
      } else {
        setActiveIndex(undefined);
        dispatch(deleteScenario());
        dispatch(deleteSelectedTestCaseTemplate());
      }
    }
  }, [dispatch, setActiveIndex, filteredScenarios, testCaseTemplates]);

  useEffect(() => {
    if (!!scenarios) {
      setFilteredScenarios(scenarios.filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase())));
    }
  }, [setFilteredScenarios, searchValue, scenarios]);

  useEffect(() => {
    dispatch(deleteScenarioExecuteResponse());
  }, [dispatch, selectedScenario]);

  const changeActiveScenario = useCallback((scenarioIndex: number): void => {
    setActiveIndex((activeIndex) => {
      if (scenarioIndex !== activeIndex) {
        dispatch(setScenario({scenario: filteredScenarios[scenarioIndex], templates: testCaseTemplates || [], selectedVariables}));
        return scenarioIndex;
      } else {
        dispatch(deleteScenario());
        dispatch(deleteSelectedTestCaseTemplate());
        return undefined;
      }
    });
  }, [dispatch, setActiveIndex, filteredScenarios, testCaseTemplates, selectedVariables]);

  const clickRunScenario = useCallback(() => {
    dispatch(setScenarioExecuteResponse(selectedTestCases.map(testCase => ({
      statusCode: 0,
      responseBody: "",
      time: 0,
      internalId: testCase.internalId
    }))));
  }, [dispatch, selectedTestCases])

  const closeScenarioModal = useCallback((isSave: boolean, createId?: number): void => {
    setShowScenarioModal(false);
    setEditScenario(undefined);
    if (isSave) {
      if (!!createId) {
        const newSelectedScenario = { ...selectedScenario };
        newSelectedScenario.id = createId;
        dispatch(setScenario({ scenario: newSelectedScenario, templates: testCaseTemplates || [], selectedVariables }));
      }
      refetchScenarios();
    }
  }, [dispatch, setShowScenarioModal, setEditScenario, refetchScenarios, selectedScenario, testCaseTemplates, selectedVariables]);

  const clickAddScenario = useCallback((): void => {
    setShowScenarioModal(true);
  }, [setShowScenarioModal]);

  const clickEditScenario = useCallback((scenario: Scenario): void => {
    setEditScenario(scenario);
    setShowScenarioModal(true);
  }, [setEditScenario, setShowScenarioModal]);

  const cancelChangesScenario = useCallback((): void => {
    const newSelectedScenario = filteredScenarios.find(item => item.id === selectedScenario.id);
    if (newSelectedScenario) {
      dispatch(setScenario({ scenario: newSelectedScenario, templates: testCaseTemplates || [], selectedVariables }));
    }
  }, [dispatch, filteredScenarios, selectedScenario, testCaseTemplates, selectedVariables]);

  const onChangeSearch = useCallback((value: string): void => {
    setSearchValue(value);
  }, [setSearchValue])

  const updateExecuteResponse = useCallback((executeResponse: TransformExecuteResponse) => {
    dispatch(updateScenarioExecuteResponse(executeResponse));
  }, [dispatch])

  const disabledRunButton = useMemo(() => {
    return selectedTestCases.some(item => item.isDeleted || item.isUpdated || !item.internalId) || selectedTestCases.length === 0;
  }, [selectedTestCases]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.mainContainer}>
          <div className={styles.header}>
            <div className={styles.search}>
              <SearchInput value={searchValue} onChange={onChangeSearch}/>
            </div>
            <div className={styles.addScenario} onClick={clickAddScenario}>
              <AddIcon/>
              {t("scenario.addNew")}
            </div>
          </div>
          <div className={styles.scenarios}>
            <Accordion activeClassName={styles.active}
                       maxHeight={400}
                       activeIndex={activeIndex}
                       customClick={true}
                       data={!!testCaseTemplates
                         ? filteredScenarios.map((scenario, index) => {
                           return {
                             title: (<ScenarioItem scenario={scenario}
                                                   testCaseTemplates={testCaseTemplates}
                                                   arrowClass={styles.arrow}
                                                   onClick={() => changeActiveScenario(index)}
                                                   onClickEdit={clickEditScenario}/>),
                             child: (
                               <ScenarioTestCases scenario={scenario}
                                                  testCaseTemplates={testCaseTemplates}
                                                  refetchScenarios={refetchScenarios}
                                                  cancelChanges={cancelChangesScenario}/>
                             )
                           }
                         })
                         : []}/>
          </div>
        </div>
        <div className={styles.bottomContainer}>
          <RunTestCasesButton selectedTestCases={selectedTestCases}
                              clickRun={clickRunScenario}
                              beforeRunIfWasLaunched={transformTestCases}
                              testCaseTemplates={testCaseTemplates || []}
                              updateExecuteResponse={updateExecuteResponse}
                              disabledRunButton={disabledRunButton}/>
        </div>
      </div>
      {showScenarioModal && (<ScenarioModal onClose={closeScenarioModal} scenario={editScenario}/>)}
    </>

  )
}